(function () {
  'use strict';

  const ResortFinder = {

    state: {
      selectedHandle:      null,
      selectedVariantId:   null,
      selectedPrice:       null,
      selectedCompareAt:   null,
      adding:              false,
      selectedProductType: null,
      carouselIndex:       0,
      carouselLength:      0,
    },

    productTypes: [
      { key: 'Freeform Blanket – AOP',   label: 'Blanket',     icon: '🛏️', badge: 'Best Seller' },
      { key: 'Flannel Blanket Poncho - AOP', label: 'Poncho',  icon: '🧥', badge: null },
      { key: 'Snug Hoodie',              label: 'Snug Hoodie', icon: '👕', badge: null },
      { key: 'Beach Towel',              label: 'Beach Towel', icon: '🏖️', badge: null },
    ],

    // All products fetched from collection API — no Liquid loop needed
    allProducts: [],

    // ── Boot ────────────────────────────────────────────────────────────────

    async init() {
      this.typeSelector  = document.getElementById('rf-type-selector');
      this.finderSection = document.getElementById('rf-finder-section');
      this.searchInput   = document.getElementById('rf-search');
      this.browseSection = document.getElementById('rf-browse');
      this.searchResults = document.getElementById('rf-search-results');
      this.auTab         = document.getElementById('rf-tab-au');
      this.nzTab         = document.getElementById('rf-tab-nz');
      this.resortGrid    = document.getElementById('rf-resort-grid');
      this.productPanel  = document.getElementById('rf-product-panel');
      this.atcBtn        = document.getElementById('rf-atc-btn');

      if (!this.typeSelector) return;

      this.currentCountry = 'au';

      // Fetch all products from collection API — bypasses Liquid 50-product cap
      const collectionHandle = document.getElementById('rf-collection-handle').dataset.handle;
      await this.fetchAllProducts(collectionHandle);

      // Wire events
      this.auTab.addEventListener('click',  () => this.switchCountry('au'));
      this.nzTab.addEventListener('click',  () => this.switchCountry('nz'));
      this.atcBtn.addEventListener('click', () => this.addToCart());
      this.searchInput.addEventListener('input', (e) => this.handleSearch(e.target.value));
      document.querySelectorAll('.rf-faq-question').forEach(q => {
        q.addEventListener('click', () => this.toggleFaq(q));
      });

      this.renderTypeSelector();
    },

    // ── Collection API fetch ─────────────────────────────────────────────────

    async fetchAllProducts(handle) {
      const url = `/collections/${handle}/products.json?limit=250`;
      try {
        const res  = await fetch(url);
        const data = await res.json();
        this.allProducts = data.products || [];
        console.log(`[ResortFinder] Fetched ${this.allProducts.length} products from collection`);

        // Log country breakdown
        const byCountry = {};
        const byType    = {};
        this.allProducts.forEach(p => {
          const country = this.getProductCountry(p);
          const type    = p.product_type;
          byCountry[country || 'unknown'] = (byCountry[country || 'unknown'] || 0) + 1;
          byType[type || 'unknown']       = (byType[type || 'unknown'] || 0) + 1;
        });
        console.log('[ResortFinder] By country:', byCountry);
        console.log('[ResortFinder] By type:', byType);
      } catch (err) {
        console.error('[ResortFinder] Failed to fetch collection products:', err);
      }
    },

    // ── Country detection ───────────────────────────────────────────────────
    // Uses tags first (country:au / country:nz), falls back to metafield-style
    // tag patterns like "New Zealand", "Australia", or the resort_country metafield
    // baked into a data attribute on the page element.

    getProductCountry(product) {
      const tags = product.tags || [];

      // Priority 1: rf-country: tag
      const rfCountry = tags.find(t => t.toLowerCase().startsWith('rf-country:'));
      if (rfCountry) return rfCountry.split(':')[1].trim().toLowerCase();

      // Priority 2: explicit country: tags
      if (tags.includes('country:au') || tags.includes('Country:AU')) return 'au';
      if (tags.includes('country:nz') || tags.includes('Country:NZ')) return 'nz';

      // Priority 3: plain country name tags
      if (tags.includes('Australia')) return 'au';
      if (tags.includes('New Zealand')) return 'nz';

      // Priority 4: resort_country metafield baked into data attribute (if present)
      const el = document.querySelector(`[data-handle="${product.handle}"]`);
      if (el && el.dataset.country) return el.dataset.country;

      // Last resort: check product title
      const title = (product.title || '').toLowerCase();
      if (title.includes('australia') || title.includes(', nsw') || title.includes(', vic') || title.includes(', tas')) return 'au';
      if (title.includes('new zealand') || title.includes(', nz')) return 'nz';

      return null;
    },

    getProductName(product) {
      const tags = product.tags || [];

      // Priority 1: rf: tag e.g. "rf:Ben Lomond Ski Field"
      const rfTag = tags.find(t => t.toLowerCase().startsWith('rf:'));
      if (rfTag) return rfTag.split(':')[1].trim();

      // Priority 2: resort: tag — auto-insert spaces before capitals
      // e.g. "resort:BenLomondSkiField" → "Ben Lomond Ski Field"
      const resortTag = tags.find(t => t.toLowerCase().startsWith('resort:'));
      if (resortTag) {
        return resortTag.split(':')[1].trim()
          .replace(/([A-Z])/g, ' $1')
          .trim();
      }

      // Priority 3: strip known suffixes from product title
      return product.title
        .replace(/,?\s*(australia|new zealand)\s*/gi, '')
        .replace(/\s*ski resort map\s*/gi, ' ')
        .replace(/\s*(xl|freeform|printed|aop)?\s*blanket\s*/gi, '')
        .replace(/\s*(nsw|vic|tas|act|sa|wa|nt|qld)\s*/gi, '')
        .replace(/\s*(otago|canterbury|wanaka|ruapehu|nelson|mackenzie|queenstown|southland|hawkes bay)\s*/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
    },

    getProductState(product) {
      const tags = product.tags || [];

      // Priority 1: rf-state: tag e.g. "rf-state:Tasmania"
      const rfState = tags.find(t => t.toLowerCase().startsWith('rf-state:'));
      if (rfState) return rfState.split(':')[1].trim();

      // Priority 2: state: tag
      const stateTag = tags.find(t => t.toLowerCase().startsWith('state:'));
      if (stateTag) return stateTag.split(':')[1].trim();

      return '';
    },

    // ── Product type selector ────────────────────────────────────────────────

    renderTypeSelector() {
      const grid = document.getElementById('rf-type-grid');
      if (!grid) return;

      const availableTypes = new Set(this.allProducts.map(p => p.product_type));
      const toShow = this.productTypes.filter(t => availableTypes.has(t.key));

      console.log('[ResortFinder] Available types:', [...availableTypes]);

      grid.innerHTML = toShow.map(t => `
        <button class="rf-type-btn" data-type="${t.key}" type="button">
          ${t.badge ? `<span class="rf-type-badge">${t.badge}</span>` : ''}
          <span class="rf-type-icon">${t.icon}</span>
          <span class="rf-type-label">${t.label}</span>
        </button>`).join('');

      grid.querySelectorAll('.rf-type-btn').forEach(btn => {
        btn.addEventListener('click', () => this.selectProductType(btn.dataset.type));
      });
    },

    selectProductType(type) {
      this.state.selectedProductType = type;
      this.state.selectedHandle      = null;

      document.querySelectorAll('.rf-type-btn').forEach(btn => {
        btn.classList.toggle('is-selected', btn.dataset.type === type);
      });

      this.finderSection.hidden    = false;
      this.productPanel.hidden     = true;
      this.currentCountry          = 'au';
      this.auTab.classList.add('is-active');
      this.nzTab.classList.remove('is-active');

      this.renderGrid('au');

      setTimeout(() => {
        this.finderSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    },

    // ── Resort grid ──────────────────────────────────────────────────────────

    getResorts(country) {
      return this.allProducts
        .filter(p => {
          const matchesType    = !this.state.selectedProductType || p.product_type === this.state.selectedProductType;
          const matchesCountry = this.getProductCountry(p) === country;
          return matchesType && matchesCountry;
        })
        .map(p => ({
          name:   this.getProductName(p),
          state:  this.getProductState(p),
          handle: p.handle,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    renderGrid(country) {
      const resorts = this.getResorts(country);
      this.resortGrid.innerHTML = resorts.length
        ? resorts.map(r => `
            <button class="rf-resort-btn${this.state.selectedHandle === r.handle ? ' is-selected' : ''}"
                    data-handle="${r.handle}"
                    type="button">
              ${r.name}
              ${r.state ? `<span class="rf-resort-state">${r.state}</span>` : ''}
            </button>`).join('')
        : `<p class="rf-no-results">No resorts found</p>`;

      this.resortGrid.querySelectorAll('.rf-resort-btn').forEach(btn => {
        btn.addEventListener('click', () => this.selectResort(btn.dataset.handle));
      });
    },

    switchCountry(country) {
      this.currentCountry = country;
      this.auTab.classList.toggle('is-active', country === 'au');
      this.nzTab.classList.toggle('is-active', country === 'nz');
      this.renderGrid(country);
    },

    handleSearch(val) {
      const trimmed = val.trim().toLowerCase();
      if (!trimmed) {
        this.searchResults.innerHTML = '';
        this.searchResults.hidden    = true;
        this.browseSection.hidden    = false;
        return;
      }

      const all = [
        ...this.getResorts('au'),
        ...this.getResorts('nz'),
      ].filter(r => r.name.toLowerCase().includes(trimmed));

      this.browseSection.hidden    = true;
      this.searchResults.hidden    = false;
      this.searchResults.innerHTML = all.length
        ? all.map(r => `
            <button class="rf-resort-btn${this.state.selectedHandle === r.handle ? ' is-selected' : ''}"
                    data-handle="${r.handle}"
                    type="button">
              ${r.name}
              ${r.state ? `<span class="rf-resort-state">${r.state}</span>` : ''}
            </button>`).join('')
        : `<p class="rf-no-results">No resorts found — try another name</p>`;

      this.searchResults.querySelectorAll('.rf-resort-btn').forEach(btn => {
        btn.addEventListener('click', () => this.selectResort(btn.dataset.handle));
      });
    },

    // ── Product fetch & render ───────────────────────────────────────────────

    async selectResort(handle) {
      if (this.state.selectedHandle === handle) return;
      this.state.selectedHandle = handle;

      this.renderGrid(this.currentCountry);
      this.productPanel.classList.add('is-loading');
      this.productPanel.hidden = false;

      try {
        const res     = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error('Product not found');
        const product = await res.json();
        this.renderProduct(product);
      } catch (err) {
        console.error('[ResortFinder] Failed to load product:', err);
        this.productPanel.classList.remove('is-loading');
      }

      setTimeout(() => {
        this.productPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    },

    renderProduct(product) {
      const variant = product.variants[0];
      this.state.selectedVariantId = variant.id;
      this.state.selectedPrice     = variant.price;
      this.state.selectedCompareAt = variant.compare_at_price;

      // Title
      document.getElementById('rf-product-title').textContent = product.title;

      // Price
      const price     = this.formatMoney(variant.price);
      const compareAt = variant.compare_at_price && variant.compare_at_price > variant.price
        ? this.formatMoney(variant.compare_at_price) : null;

      document.getElementById('rf-product-price').textContent = price;

      const compareEl = document.getElementById('rf-compare-price');
      const saleBadge = document.getElementById('rf-sale-badge');
      if (compareAt) {
        compareEl.textContent       = compareAt;
        compareEl.style.display     = 'inline';
        saleBadge.style.display     = 'inline-block';
      } else {
        compareEl.style.display     = 'none';
        saleBadge.style.display     = 'none';
      }

      // Description — strip style blocks and HTML tags
      const descEl    = document.getElementById('rf-product-description');
      const descBlock = descEl && descEl.closest('.rf-desc-block');
      if (product.body_html && descEl) {
        const stripped = product.body_html
          .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        descEl.textContent = stripped.length > 220 ? stripped.slice(0, 220) + '…' : stripped;
        if (descBlock) descBlock.hidden = false;
      } else if (descBlock) {
        descBlock.hidden = true;
      }

      // Carousel
      this.renderCarousel(product.images || [], product.title);

      // ATC
      this.atcBtn.disabled    = !variant.available;
      this.atcBtn.textContent = variant.available ? `Add to cart — ${price}` : 'Sold out';

      this.productPanel.classList.remove('is-loading');
    },

    // ── Image carousel ───────────────────────────────────────────────────────

    renderCarousel(images, title) {
      const track       = document.getElementById('rf-carousel-track');
      const dots        = document.getElementById('rf-carousel-dots');
      const prevBtn     = document.getElementById('rf-carousel-prev');
      const nextBtn     = document.getElementById('rf-carousel-next');
      const placeholder = document.getElementById('rf-image-placeholder');

      if (!track) return;

      this.state.carouselIndex = 0;

      if (!images.length) {
        track.innerHTML = '';
        if (placeholder) {
          track.appendChild(placeholder);
          placeholder.style.display = 'flex';
        }
        prevBtn.style.display = 'none';
        nextBtn.style.display = 'none';
        dots.innerHTML        = '';
        return;
      }

      const shown = images.slice(0, 5);
      track.innerHTML = shown.map((src, i) => {
        const fullUrl = (src.startsWith('//') ? 'https:' + src : src)
          .replace(/(\.[^.?]+)(\?|$)/, '_800x800$1$2');
        return `<div class="rf-carousel-slide">
          <img src="${fullUrl}"
               alt="${title} — image ${i + 1}"
               loading="${i === 0 ? 'eager' : 'lazy'}">
        </div>`;
      }).join('');

      dots.innerHTML = shown.length > 1
        ? shown.map((_, i) =>
            `<button class="rf-carousel-dot${i === 0 ? ' is-active' : ''}"
                     data-index="${i}"
                     aria-label="Go to image ${i + 1}"></button>`).join('')
        : '';

      dots.querySelectorAll('.rf-carousel-dot').forEach(dot => {
        dot.addEventListener('click', () => this.goToSlide(parseInt(dot.dataset.index)));
      });

      if (shown.length > 1) {
        prevBtn.style.display = 'flex';
        nextBtn.style.display = 'flex';
        prevBtn.onclick = () => this.goToSlide((this.state.carouselIndex - 1 + shown.length) % shown.length);
        nextBtn.onclick = () => this.goToSlide((this.state.carouselIndex + 1) % shown.length);
      } else {
        prevBtn.style.display = 'none';
        nextBtn.style.display = 'none';
      }

      this.state.carouselLength = shown.length;
      this.initCarouselSwipe(track, shown.length);
      this.goToSlide(0);
    },

    goToSlide(index) {
      const track = document.getElementById('rf-carousel-track');
      const dots  = document.getElementById('rf-carousel-dots');
      if (!track) return;
      this.state.carouselIndex    = index;
      track.style.transform       = `translateX(-${index * 100}%)`;
      dots && dots.querySelectorAll('.rf-carousel-dot').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    },

    initCarouselSwipe(track, total) {
      let startX     = 0;
      let isDragging = false;

      // Remove old listeners by cloning
      const fresh = track.cloneNode(true);
      track.parentNode.replaceChild(fresh, track);
      const t = document.getElementById('rf-carousel-track');

      t.addEventListener('touchstart', (e) => {
        startX     = e.touches[0].clientX;
        isDragging = true;
      }, { passive: true });

      t.addEventListener('touchend', (e) => {
        if (!isDragging) return;
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) {
          this.goToSlide(diff > 0
            ? (this.state.carouselIndex + 1) % total
            : (this.state.carouselIndex - 1 + total) % total);
        }
        isDragging = false;
      }, { passive: true });
    },

    // ── Add to cart ──────────────────────────────────────────────────────────

    async addToCart() {
      if (this.state.adding || !this.state.selectedVariantId) return;
      this.state.adding = true;

      const btn          = this.atcBtn;
      const originalText = btn.textContent;
      btn.textContent    = 'Adding…';
      btn.disabled       = true;

      try {
        const res = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ id: this.state.selectedVariantId, quantity: 1 }),
        });

        if (!res.ok) throw new Error('Add to cart failed');

        btn.textContent = 'Added! ✓';

        // Trigger Rebuy smart cart drawer
        document.dispatchEvent(new CustomEvent('cart:refresh',           { bubbles: true }));
        document.dispatchEvent(new CustomEvent('rebuy:cart:item-added',  {
          bubbles: true,
          detail:  { variantId: this.state.selectedVariantId },
        }));

        this.refreshCartCount();

        setTimeout(() => {
          btn.textContent   = originalText;
          btn.disabled      = false;
          this.state.adding = false;
        }, 2500);

      } catch (err) {
        console.error('[ResortFinder] Add to cart failed:', err);
        btn.textContent   = 'Error — try again';
        btn.disabled      = false;
        this.state.adding = false;
      }
    },

    async refreshCartCount() {
      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        document.querySelectorAll('[data-cart-count], .cart-count, .header__cart-count').forEach(el => {
          el.textContent = cart.item_count;
        });
      } catch (e) { /* non-critical */ }
    },

    // ── FAQ accordion ────────────────────────────────────────────────────────

    toggleFaq(questionEl) {
      const answer = questionEl.nextElementSibling;
      const isOpen = answer.getAttribute('aria-hidden') === 'false';
      document.querySelectorAll('.rf-faq-answer').forEach(a => {
        a.setAttribute('aria-hidden', 'true');
        a.previousElementSibling.setAttribute('aria-expanded', 'false');
      });
      if (!isOpen) {
        answer.setAttribute('aria-hidden', 'false');
        questionEl.setAttribute('aria-expanded', 'true');
      }
    },

    // ── Helpers ──────────────────────────────────────────────────────────────

    formatMoney(cents) {
      const dollars = (cents / 100).toFixed(2).replace('.00', '');
      return `$${dollars} AUD`;
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ResortFinder.init());
  } else {
    ResortFinder.init();
  }

})();
